import React from 'react';
import styled, { useTheme } from 'styled-components';

import { Button, BusyIndicator, Select } from '@mollybet/ui';

import { ThemedCardLevel1, CardContents } from 'common/components/cardStyles';
import { Currency } from 'common/classes/currency';
import { TransactionMode } from '../customerHome';
import { AccountWithBalance } from 'common/state/accounts/actions';
import { Client, Wallet } from 'common/state/banking/actions';
import { transparentize } from 'polished';

const MollyImage = styled.img`
  padding: 0px 0px 20px 0px;
  height: 50px;
  width: 144px;
  align-self: flex-start;
`;

const BalanceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  padding: 0.5rem;
  margin-bottom: 1rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  margin: 20px 0px 5px;
`;

const AccountSelector = styled(Select)`
  width: 100%;
  margin-bottom: 0.5rem;
`;

const StyledButton = styled(Button)`
  flex: 1;
  margin: 0px 1em;

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }
`;

const CenteredBusyIndicator = styled(BusyIndicator)`
  align-self: center;
`;

const SectionTitle = styled.div`
  font-size: 0.875rem;
  border-top: 1px solid ${({ theme }) => transparentize(0.75, theme.foreground)};
  padding: 1rem 0.5rem 0.5rem;

  &:first-child {
    border-top-width: 0;
  }
`;

const PrimaryButton = styled(StyledButton).attrs({ variant: 'primary' })``;

export function AccountCard({
  setTransactionMode,
  transactionMode,
  clients,
  wallets,
  accounts,
}: {
  setTransactionMode: (mode: TransactionMode) => void;
  transactionMode: TransactionMode;
  clients: Client[];
  wallets: Wallet[];
  accounts: AccountWithBalance[];
}) {
  const theme = useTheme();

  const accountOptions = React.useMemo(
    () =>
      accounts?.map((account) => ({ value: account, label: account.username })),
    [accounts]
  );
  const [selectedAccount, setSelectedAccount] =
    React.useState<null | ElementType<typeof accountOptions>>(null);

  React.useEffect(() => {
    if (accountOptions) {
      setSelectedAccount(accountOptions[0]);
    }
  }, [accountOptions]);

  const depositToggle = () =>
    setTransactionMode(transactionMode === 'deposit' ? null : 'deposit');
  const withdrawToggle = () =>
    setTransactionMode(transactionMode === 'withdraw' ? null : 'withdraw');

  return (
    <ThemedCardLevel1 style={{ display: 'inline-block', marginRight: '1rem' }}>
      <CardContents>
        {!clients || !wallets ? (
          <CenteredBusyIndicator width={64} />
        ) : (
          <>
            <MollyImage src={theme.images.molly} />
            <SectionTitle>Account</SectionTitle>
            <AccountSelector
              shrinked
              value={selectedAccount}
              options={!!accounts && accountOptions}
              onChange={(e) => setSelectedAccount(e)}
            />
            <BalanceGrid style={{ marginBottom: 0 }}>
              <div>Account balance</div>
              {selectedAccount
                ? new Currency(
                    selectedAccount?.value.balance,
                    selectedAccount?.value.currency
                  ).toElement()
                : 'No account'}
              <div>Account credit</div>
              {selectedAccount
                ? new Currency(
                    selectedAccount?.value.credit,
                    selectedAccount?.value.currency
                  ).toElement()
                : 'No account'}
            </BalanceGrid>
            <ButtonContainer>
              <PrimaryButton onClick={depositToggle} disabled={!wallets.length}>
                Deposit
              </PrimaryButton>
              <PrimaryButton
                onClick={withdrawToggle}
                disabled={!wallets.length}
              >
                Withdraw
              </PrimaryButton>
            </ButtonContainer>{' '}
          </>
        )}
      </CardContents>
    </ThemedCardLevel1>
  );
}
